import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { PredictPatient, PredictProgram, WorkflowStage } from '~/types/predict';
import ProgramFitCard from './ProgramFitCard';

/**
 * Program Fit & Factors list — all programs ranked by fit score, each with its
 * decision factors. Selection is enabled only while the patient is in the
 * Predict (triage) stage, matching the prior behavior. Extracted verbatim from
 * the former Predict detail "Program Fit & Factors" tab.
 */
const ProgramFitFactorsList: React.FC<{ patient: PredictPatient }> = ({ patient }) => {
  const { visiblePrograms, selectProgram, transitionWorkflow, updatePatient } = usePredictContext();

  const canSelect = patient.currentStage === WorkflowStage.TRIAGE;

  // Selecting a program advances the patient from Predict to Qualify.
  const handleSelect = (program: PredictProgram) => {
    selectProgram(patient.id, program);
    transitionWorkflow(patient.id, WorkflowStage.ELIGIBILITY, 'Program selected — advanced to Qualify');
  };

  if (!patient.programScores || !patient.programDecisionFactors) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Disposition Scores Not Available</Typography>
        <Typography variant="body2" color="text.secondary">
          Program scoring data is not available for this patient.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        All programs ranked by fit score, with decision factors explaining each score.
      </Typography>
      {Object.entries(patient.programScores)
        .filter(([program]) => visiblePrograms.includes(program as PredictProgram))
        .map(([program, score]) => ({ program: program as PredictProgram, score: score as number }))
        .sort((a, b) => b.score - a.score)
        .map(({ program, score }) => (
          <ProgramFitCard
            key={program}
            program={program}
            score={score}
            decisionFactors={patient.programDecisionFactors![program] || []}
            isRecommended={program === patient.recommendedProgram}
            isSelected={program === patient.selectedProgram}
            onSelect={canSelect ? handleSelect : undefined}
            onClearSelection={canSelect ? () => updatePatient(patient.id, { selectedProgram: undefined }) : undefined}
          />
        ))}
    </Box>
  );
};

export default ProgramFitFactorsList;
